/* eslint-disable no-unused-vars */
import * as d3 from 'd3';
import _ from 'lodash';
import { swoopyDrag } from '../swoopy-drag';
import { wordwrap } from 'd3-jetpack';
import * as moment from 'moment';
import data from '../../../data/data.json';
import { customDateFormat } from '../utils/chart-utils';

// function for creating sales tax revenue chart
function render() {
  // a reference to the default graphic container, change if needed
  const container = d3.select('#sales-tax-revenue-chart');

  // a helper function to grab the container's width
  const getFrameWidth = () => container.node().offsetWidth;

  // a helper function to clear the container of its contents
  const clearContainer = () => container.html('');

  clearContainer();

  //set margins
  let margin = {
    top: 15,
    left: 55,
    bottom: 40,
  };

  let smallbreakPoint = 544,
    mediumBreakPoint = 768;

  // grab the data we need
  let revData = data.sales_tax_revenues;

  // grab the latest data
  const latest = revData.slice(-1)[0];
  const latest_month = apMonths.get(
    parseInt(moment(latest.calendar_date, 'MMM YYYY').format('M'))
  ); // this field is later changed to a moment date + time objects
  const latest_pct = (latest.pct_change_yoy * 100).toFixed(1);
  const latest_total = (latest.value / 1000000000).toFixed(1);

  // map the data, set the field, make it into an array so we can get the extent of the pct_change_yoy_raw field
  const dataObj = d3.map(); // d3.map();

  // sets the date
  revData.forEach(d => dataObj.set(d.calendar_date, d.pct_change_yoy));

  // changing to moment date+time objects
  revData.forEach(d => (d.date = moment(d.calendar_date, 'MMMM YYYY')));

  // filter for after a date
  revData = revData.filter(d =>
    d.date.isSameOrAfter(moment('January 2019', 'MMMM YYYY'))
  );

  // grab date range
  let dateRange = d3.extent(revData, d => d.date),
    totalsMax = d3.max(revData, d => d.pct_change_yoy);

  const dates = {
    begin: dateRange[0],
    end: dateRange[1],
    pandemicRecessionBegin: moment('2020-03-01'),
    pandemicRecessionEnd: dateRange[1],
  };

  let frameWidth = getFrameWidth(),
    frameHeight;

  if (frameWidth > smallbreakPoint) {
    frameHeight = 400;
    margin.right = 115;
  } else {
    frameHeight = 350;
    margin.right = 105;
  }

  let chartWidth = frameWidth - margin.left - margin.right,
    chartHeight = frameHeight - margin.bottom;

  //  set scale domains
  let x = d3
    .scaleBand()
    .domain(revData.map(d => d.date))
    .range([0, chartWidth]);
  let y = d3
    .scaleLinear()
    .domain([-totalsMax, totalsMax])
    .range([chartHeight, 0])
    .nice();

  // append svg
  let svg = container.append('svg'),
    g = svg.append('g').attr('class', 'unemployment-rate-line-chart');

  svg.attr('width', frameWidth).attr('height', frameHeight);
  g.attr('width', chartWidth).attr(
    'transform',
    `translate(${margin.left},${margin.top})`
  );

  let xAxis = g.append('g').attr('class', 'axis axis--x'),
    yAxis = g.append('g').attr('class', 'axis axis--y');

  drawEventArea(
    dates.pandemicRecessionBegin,
    dates.pandemicRecessionEnd,
    'Pandemic',
    dates.pandemicRecessionBegin.clone().add(1, 'months'),
    'start'
  );

  let stackedData = d3.stack().keys(['pct_change_yoy'])(revData);

  let barGroups = g
    .append('g')
    .selectAll('g')
    .data(stackedData)
    .enter()
    .append('g')
    .attr('class', 'bar-groups');

  let barRects = barGroups
    .selectAll('rect')
    .data(function(d) {
      return d;
    })
    .enter()
    .append('rect');

  barRects
    .attr('fill', d => {
      let diff = y(d[0]) - y(d[1]);
      if (diff >= 0) return '#529aad';
      if (diff < 0) return '#d13926';
    })
    .attr('x', function(d) {
      return x(d.data.date);
    })
    .attr('y', function(d) {
      let diff = y(d[0]) - y(d[1]);
      if (diff >= 0) return y(d[1]);
      if (diff < 0) return y(d[0]);
    })
    .attr('height', function(d) {
      let diff = y(d[0]) - y(d[1]);
      return Math.abs(diff);
    })
    .attr('width', x.bandwidth());

  xAxis
    .call(
      d3
        .axisBottom(x)
        .tickValues(
          x.domain().filter(function(d) {
            if (d.format('M') == 1) return d;
          })
        )
        .tickFormat(customDateFormat)
    )
    .attr('transform', `translate(0,${frameHeight - margin.bottom})`);

  yAxis.call(
    d3
      .axisLeft(y)
      .ticks(5)
      .tickSize(-chartWidth)
      .tickFormat(d => {
        if (d == 0) return '0%';
        else
          return `${d > 0 ? '+' : ''}${d3
            .format('.0%')(d)
            .replace(/G/, 'B')}`;
      })
      .tickPadding(20)
  );

  // label the end point
  let labels = g.append('g').attr('class', 'labeled-elements');

  // additional label?
  let previousYearAnno = labels
    .append('text')
    .attr('font-size', frameWidth > smallbreakPoint ? '13px' : '12px')
    .attr('y', y(-0.065))
    .attr('display', frameWidth > smallbreakPoint ? 'inital' : 'none');

  previousYearAnno.each(function() {
    d3.select(this)
      .text('')
      .tspans(
        wordwrap(
          'Before the pandemic, revenue was consistently higher than what was collected the previous year.',
          frameWidth > mediumBreakPoint ? 30 : 20
        )
      )
      .attr('dx', x(moment('2019 April', 'YYYY MMM')));
  });

  let labelElements = labels
    .selectAll('g')
    .data(getLabeledData(revData, ['']))
    .enter()
    .append('g')
    .attr('class', 'label-element');

  labelElements
    .attr('transform', d => `translate(${x(d.date)},${y(d.pct_change_yoy)})`)
    .attr('font-size', frameWidth > smallbreakPoint ? '13px' : '12px');

  let labelText = labelElements.append('g').attr('class', 'label-text');

  labelText
    .append('text')
    .text(d => {
      if (d.pct_change_yoy == 0) {
        return 'No change';
      } else {
        let change;
        if (d.pct_change_yoy > 0) change = 'Up';
        if (d.pct_change_yoy < 0) change = 'Down';

        return `${change} ${Math.abs(
          parseFloat((d.pct_change_yoy * 100).toFixed(1))
        )}%`;
      }
    })
    .attr('font-weight', 700);

  labelText
    .attr('transform', (d, i) => {
      if (i === 0 && frameWidth < smallbreakPoint) {
        return 'translate(18, 0)';
      } else return 'translate(35, 0)';
    })
    .attr('text-anchor', 'start');

  let dateAnno = labelText.append('text');
  dateAnno.attr('y', frameWidth > smallbreakPoint ? 17 : 13);
  dateAnno.each(function() {
    d3.select(this)
      .text('')
      .tspans(wordwrap(`from last ${latest_month}`, 15));
  });

  let revAnno = labelText.append('text').attr('font-weight', 700);
  revAnno.attr('y', frameWidth > smallbreakPoint ? 40 : 35);
  revAnno.each(function() {
    d3.select(this)
      .text('')
      .tspans(wordwrap(`$${latest_total} billion`, 15));
  });

  let rev2Anno = labelText.append('text');
  rev2Anno.attr('y', frameWidth > smallbreakPoint ? 55 : 50);
  rev2Anno.each(function(d) {
    d3.select(this)
      .text('')
      .tspans(
        wordwrap(
          `collected in ${d.calendar_date}`,
          frameWidth > smallbreakPoint ? 15 : 15
        )
      );
  });

  let july2020 = revData.find(d => d.calendar_date == 'July 2020');

  // arrow marker, need to append to the svg first to use
  svg
    .append('marker')
    .attr('id', 'arrow')
    .attr('viewBox', '-10 -10 20 20')
    .attr('markerWidth', 10)
    .attr('markerHeight', 10)
    .attr('fill', 'hsl(0, 0%, 29%)')
    .attr('orient', 'auto-start-reverse')
    .append('path')
    .attr('d', 'M-6.75,-6.75 L 0,0 L -6.75,6.75');

  let swoopySel = svg.append('g').attr('class', 'swoopy-drag-lines');

  // annotations for swoopyDrag
  let annotations = [
    {
      x: july2020.date,
      y: july2020.pct_change_yoy,
      path:
        frameWidth > smallbreakPoint
          ? 'M82,18C93,11,95,0,92,-12'
          : 'M68,22C78,14,78,2,74,-10',
      text:
        'Retail spending and an influx of federal pandemic benefits increased revenue.',
      textOffset: frameWidth > smallbreakPoint ? [100, -63] : [88, -60],
    },
  ];

  let swoopy = swoopyDrag()
    .x(d => x(d.x))
    .y(d => y(d.y))
    .draggable(false)
    .annotations(annotations);

  swoopySel
    .call(swoopy)
    .attr('font-size', frameWidth > smallbreakPoint ? '13px' : '12px');

  swoopySel.selectAll('text').each(function(d) {
    d3.select(this)
      .text('')
      .tspans(wordwrap(d.text, 20))
      .attr('text-anchor', 'end');
  });

  swoopySel.selectAll('path').attr('marker-start', 'url(#arrow)');

  function drawEventArea(
    beginDate,
    endDate,
    labelText,
    labelTextDate,
    labelAnchor
  ) {
    let labels = g.append('g').attr('class', 'area-label-elements');

    let elements = labels
      .selectAll('g')
      .data(getLabeledData(revData, ['']))
      .enter()
      .append('g')
      .attr('class', 'area-label-element');

    elements
      .append('rect')
      .attr('class', 'column')
      .attr('fill', '#000')
      .attr('opacity', 0.09)
      .attr('x', x(moment(beginDate, 'YYYY MMM')))
      .attr('y', y(0.3))
      .attr(
        'width',
        x(moment(endDate, 'YYYY MMM')) -
          x(moment(beginDate, 'YYYY MMM')) +
          x.bandwidth()
      )
      .attr('height', y(-0.3) - y(0.3));

    let text = elements
      .append('g')
      .attr('class', 'area-text')
      .attr('text-anchor', labelAnchor);

    text
      .append('text')
      .text('')
      .tspans(wordwrap(labelText, frameWidth > smallbreakPoint ? 30 : 10));

    text.attr(
      'transform',
      'translate(' +
        (frameWidth > smallbreakPoint ? x(labelTextDate) : x(labelTextDate)) +
        ',' +
        y(-0.27) +
        ')'
    );
  }
}

let apMonths = new Map([
  [1, 'Jan.'],
  [2, 'Feb.'],
  [3, 'March'],
  [4, 'April'],
  [5, 'May'],
  [6, 'June'],
  [7, 'July'],
  [8, 'Aug.'],
  [9, 'Sept.'],
  [10, 'Oct.'],
  [11, 'Nov.'],
  [12, 'Dec.'],
]);

function getLabeledData(data, dates) {
  // always annotate the latest datapoint
  let labeledData = [data[data.length - 1]];
  dates.forEach(date => {
    date = moment(date, 'YYYY MMM'); // format needs to match date format in the data
    let foundData = data.find(d => d.date.isSame(date));
    if (foundData) {
      labeledData.push(foundData);
    }
  });
  return labeledData;
}

export default render;
